import { useState, useEffect } from 'react';
import { Target, Users, Globe, Mail, TrendingUp, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCampaigns, useLeads, useSites } from '@/hooks/useFirestore';
import { StatCard } from '@/components/ui/StatCard';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { formatDate } from '@/lib/utils';
import { PLAN_CONFIGS } from '@shared/types';
import { countContactedBusinesses } from '@/lib/outreach';

// Animated counter component
function AnimatedValue({ value, suffix = '', className = '' }: { value: number; suffix?: string; className?: string }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 1500;
    const startTime = Date.now();

    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(eased * value));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value]);

  return <span className={className}>{displayValue}{suffix}</span>;
}

export function DashboardPage() {
  const { userData } = useAuth();
  const { data: campaigns } = useCampaigns();
  const { data: leads } = useLeads();
  const { data: sites } = useSites();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const activeSites = sites?.filter((s) => s.status === 'sent' || s.status === 'sold') || [];

  // Calculate earnings for this week only
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const weeklyEarnings = activeSites
    .filter((s) => {
      if (s.status !== 'sold') return false;
      const soldDate = s.soldAt?.toDate?.() ?? s.updatedAt?.toDate?.() ?? null;
      return soldDate && soldDate >= oneWeekAgo;
    })
    .reduce((sum, s) => sum + ((s.price || 29900) / 100), 0);
  // Filter out leads that already have sites generated
  const recentLeads = leads?.filter((l) => !l.siteGenerated).slice(0, 5) || [];

  return (
    <div
      className={`space-y-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Welcome header */}
      <div
        className="relative p-6 rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(91,58,58,0.1) 0%, rgba(0,0,0,0.5) 100%)',
          border: '1px solid rgba(91,58,58,0.2)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-primary-400" />
            <span className="text-sm text-primary-400 font-medium">Command Center</span>
          </div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">
            Welcome Back
          </h1>
          <p className="text-gray-400 mt-1">Your empire awaits. Here's your conquest status.</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            title: 'Lifetime Campaigns',
            value: campaigns?.length || 0,
            icon: <Target className="w-6 h-6" />,
            delay: 0,
          },
          {
            title: 'Total Leads',
            value: leads?.length || 0,
            subtitle: `${userData?.usage?.leadsUsed || 0}/${userData?.quotas?.leadsLimit || 50} used`,
            icon: <Users className="w-6 h-6" />,
            delay: 0.1,
          },
          {
            title: 'Sites Created',
            value: sites?.length || 0,
            subtitle: `${userData?.usage?.sitesUsed || 0}/${userData?.quotas?.sitesLimit || 10} used`,
            icon: <Globe className="w-6 h-6" />,
            delay: 0.2,
          },
          {
            title: 'Businesses Contacted',
            value: countContactedBusinesses(leads, sites),
            icon: <Mail className="w-6 h-6" />,
            delay: 0.3,
          },
        ].map((stat, i) => (
          <div
            key={i}
            style={{
              animation: `fadeInUp 0.5s ease-out ${stat.delay}s both`,
            }}
          >
            <StatCard
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
            />
          </div>
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard
          className="transform transition-all duration-300 hover:scale-[1.01]"
          style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white font-display">Recent Leads</h2>
            <Badge variant="secondary">{leads?.length || 0} total</Badge>
          </div>

          {recentLeads.length > 0 ? (
            <div className="space-y-4">
              {recentLeads.map((lead, index) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5 hover:border-primary-500/30 hover:bg-white/10 transition-all duration-200"
                  style={{
                    animation: `fadeInLeft 0.3s ease-out ${index * 0.05}s both`,
                  }}
                >
                  <div>
                    <p className="font-medium text-white">{lead.businessName}</p>
                    <p className="text-sm text-gray-500">{lead.niche} • {lead.location}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {lead.hasWebsite && (
                      <Badge variant="warning">Has Website</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-400">No leads yet</p>
              <p className="text-sm text-gray-500">Create a campaign to start your conquest</p>
            </div>
          )}
        </GlassCard>

        <GlassCard
          className="transform transition-all duration-300 hover:scale-[1.01]"
          style={{ animation: 'fadeInUp 0.5s ease-out 0.5s both' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white font-display">Quota Usage</h2>
            <Badge variant={userData?.planId ? 'accent' : 'secondary'}>
              {userData?.planId ? PLAN_CONFIGS[userData.planId].name : 'Starter'} Plan
            </Badge>
          </div>

          <div className="space-y-6">
            {[
              {
                label: 'Leads',
                used: userData?.usage?.leadsUsed || 0,
                limit: userData?.quotas?.leadsLimit || 50,
                color: 'from-primary-500 to-primary-400',
              },
              {
                label: 'Sites',
                used: userData?.usage?.sitesUsed || 0,
                limit: userData?.quotas?.sitesLimit || 10,
                color: 'from-accent-500 to-accent-400',
              },
            ].map((quota, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">{quota.label}</span>
                  <span className="text-white font-medium">
                    <AnimatedValue value={quota.used} /> / {quota.limit}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-navy-800/50 overflow-hidden">
                  <div
                    className={`h-3 rounded-full bg-gradient-to-r ${quota.color} transition-all duration-1000`}
                    style={{
                      width: `${Math.min((quota.used / quota.limit) * 100, 100)}%`,
                      boxShadow: `0 0 10px rgba(91,58,58,0.3)`,
                    }}
                  />
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-500 pt-2 border-t border-white/10">
              Quotas reset on your billing date each month. View in Settings → Billing.
            </p>
          </div>
        </GlassCard>
      </div>

      {/* Bottom grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard
          className="lg:col-span-2 transform transition-all duration-300 hover:scale-[1.01]"
          style={{ animation: 'fadeInUp 0.5s ease-out 0.6s both' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white font-display">Recent Sites</h2>
          </div>

          {sites && sites.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sites.slice(0, 4).map((site, index) => (
                <div
                  key={site.id}
                  className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-primary-500/30 hover:bg-white/10 transition-all duration-200"
                  style={{
                    animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">{site.name}</p>
                      <p className="text-xs text-gray-500">{site.templateType}</p>
                    </div>
                    <Badge variant={site.status === 'sold' ? 'success' : site.status === 'sent' ? 'accent' : 'secondary'}>
                      {site.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-400 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {site.views || 0} views
                    </span>
                    <span className="text-gray-500">{formatDate(site.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-400">No sites created yet</p>
              <p className="text-sm text-gray-500">Generate a site from your leads</p>
            </div>
          )}
        </GlassCard>

        <GlassCard
          className="transform transition-all duration-300 hover:scale-[1.01]"
          style={{ animation: 'fadeInUp 0.5s ease-out 0.7s both' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white font-display">Quick Stats</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                label: 'Conversion Rate (All Time)',
                value: leads && leads.length > 0
                  ? Math.round(((sites?.length || 0) / leads.length) * 100)
                  : 0,
                suffix: '%',
                gradient: 'from-primary-500/20 to-primary-500/5',
                border: 'border-primary-500/30',
              },
              {
                label: 'Earnings this Week',
                value: weeklyEarnings,
                prefix: '$',
                suffix: '',
                gradient: 'from-accent-500/20 to-accent-500/5',
                border: 'border-accent-500/30',
              },
              {
                label: 'Leads w/o Website',
                value: leads?.filter((l) => !l.hasWebsite).length || 0,
                suffix: '',
                gradient: 'from-gray-500/20 to-gray-500/5',
                border: 'border-gray-500/30',
              },
            ].map((stat, i) => (
              <div
                key={i}
                className={`p-4 rounded-xl bg-gradient-to-r ${stat.gradient} border ${stat.border} hover:scale-105 transition-transform duration-200`}
              >
                <p className="text-sm text-gray-400">{stat.label}</p>
                <p className="text-3xl font-bold text-white tracking-tight">
                  {'prefix' in stat && stat.prefix}{<AnimatedValue value={stat.value} suffix={stat.suffix} />}
                </p>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
